/**
 * SalesDashboard.tsx — Sales / Home (redesign slice 3, 2026-09-27; Home tabs
 * H-3a, RULED 2026-10-18): the calm-minimal Minimal board
 * (`docs/design/calm-minimal/Minimal.dc.html`), built to ADOPTION.md §15, the
 * §22.7 ruling and the Home tabs spec §3 (docs/design/home-tabs-spec.md), not
 * to the mockup's numbers.
 *
 * The board, element by element, each on a read path this page has:
 *   · the shared header (H-3b-1, the H-3b recon rulings, Klemen 2026-10-19) — the date line (browser
 *     clock), the greeting by the LONDON time of day (homeView.greeting), the
 *     session user's first name, the per-tab lede (Overview: the INSIGHT
 *     sentence, homeView.insightSentence; My day: what is due), and the
 *     Overview · My day switch with My day's count; `?tab=` is read on load,
 *     written with replaceState while Home shows, and dropped when Home goes;
 *     the last chosen tab is remembered on the device, and the first visit of
 *     the London day opens My day when its list has open items
 *     (homeView.openingTab);
 *   · My day (H-3b-2, spec §4 and the §42 rulings) — the prioritised list
 *     and its rail (MyDay.tsx over myDayView.ts); its OPEN count is the
 *     switch's badge and the My day lede;
 *   · Team (H-4, spec §5, read-only) — shown to owner/admin or when two or
 *     more employees own deals (teamView.teamVisible); the KPI cards, the reps
 *     table, Rep detail and the insights (TeamTab.tsx over teamView.ts); with
 *     the exclude toggle on it re-reads the deals list with excludeTest;
 *   · four KPI cards, each with a ✦ note under a hairline (spec §3.1):
 *     Won this month (the current London-month bucket; the FACT delta RULED
 *     §22.7, LIKE FOR LIKE since H-1a; the partial-month label in the
 *     footnote row; the ✦ note names the open deals closing by month end, or
 *     says the month will likely end where it is);
 *     Open pipeline (the fenced Σ; count and the /reports weighted Σ; the ✦
 *     note when one deal is over 40 % of it — share, and the pipeline without
 *     it);
 *     Likely in Q{n} (the quarter's won + the weighted value of the open deals
 *     due INSIDE the quarter — /forecast's weightedDue, H-3a R-2 — with the
 *     target note: none / partial coverage, no bar / the attainment and a bar
 *     only when every rep with open deals has a target);
 *     Win rate · all time (the server's closed-cohort trio; the ✦ note names
 *     the closed test deals from /reports testDeals, R-3 — no "Exclude ›": the
 *     toggle is PR-B's, and a link with nothing behind it is not rendered);
 *   · Revenue — the SHARED chart with its empty-month stubs, the legend ("Full
 *     month" / "{Month} to date"), and the ✦ notes: a run of three or more
 *     months with no wins; the most recent month that was one deal;
 *   · Pipeline — the ring by VALUE (PipelineDonut) over the server's
 *     zero-filled spine, fills bound before zero slices drop; every stage row
 *     and the closed-won tile drill as before; the Weighted tile; "Open board ›";
 *   · Needs attention — R1's per-deal assessment, read BATCHED off the deals
 *     list (mapDeal carries it since H-3a), four rules first-match-wins
 *     (homeView.attentionRows), the top three by value × severity, a stage
 *     stripe, the reason with R1's real day counts, one action that opens the
 *     deal; "See all n ›" to the board;
 *   · Up next — FUTURE items only (spec §3.3): pending activities due from now
 *     and open deals' close dates, merged by date, three rows; a typed meeting
 *     on a weekend or bank holiday carries the amber line (no Move: PR-B's);
 *     the footer says what the card shows;
 *   · Top open deals — share bars in the STAGE colour (stageDotFor) and
 *     keyboard-reachable row drills; "See all n ›" to the board;
 *   · Inbound — the queue (fetchInboundLeads): how many wait, the oldest and
 *     how long, how many match an open deal by account_id (R-5 copy);
 *     "Triage leads ›" to the queue;
 *   · the cap sentence — every card computed from the deals list says "From
 *     the newest 200 deals only." when the list came back at the cap;
 *   · sentence case across Home ONLY (R-1): stage labels through stageDisplay,
 *     "See all", "Closed won, all time" — the DealStage values, the shell and
 *     the shared badge keep their case (the Calm Minimal track's);
 *   · the footnote — historyFootnote from the wire's historySince.
 *
 * The two H-3a reads beside the summary (the quarter's /forecast, the Inbound
 * queue) are tolerant: either failing leaves ITS card in an honest "not
 * available" state and never takes the page down. OverviewHome shares
 * fetchSalesSummary and is UNCHANGED by this slice (the HOLD control).
 *
 * RETIRED (Accounts S3, RULED 2026-10-24 — Home deferred item (d)): "Deals by
 * company" (SB-2 item 5 / SB-13) and its row drills; the Accounts list now
 * carries each account's open pipeline from the server.
 *
 * The primary action ("+ New Deal") is the shell's slot since slice 2b (4):
 * Dashboard.tsx renders the button in the bar and passes the drawer's open
 * state down; this page keeps the drawer and its refresh.
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical design system).
 */
import { useEffect, useRef, useState } from 'react'
import { AlertCircle } from 'lucide-react'
import { toast } from 'sonner'
import { LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import { useAsync, fetchSalesSummary, fetchInboundLeads, fetchSalesForecast, updateActivityDue, fetchContacts, fetchActivityHistory, completeActivity, reopenActivity, fetchDeals, ACTIVITY_HISTORY_MAX } from '../../api'
import { apiErrorMessage } from '../../../../api/client'
import { readExcludeTestDeals, writeExcludeTestDeals } from '../../../../lib/excludeTestPref'
import { readHomeTab, writeHomeTab, readHomeVisitDay, writeHomeVisitDay } from '../../../../lib/homeTabPref'
import { readSnoozed, writeSnoozed } from '../../../../lib/myDaySnoozePref'
import { compactMoney } from '../../../../lib/money'
import { NewDealDrawer } from './SalesPipeline'
import { topOpenDeals, closedThisMonthPence } from './dashboardView'
import { absentMembers, deltaLine, historyFootnote, type DeltaLine, type DeltaTone } from './trendDeltas'
import { ReportsDrillView } from './ReportsDrillView'
import { type ReportsDrill } from './reportsDrill'
import { RevenueByMonthChart } from './RevenueByMonthChart'
import { excludedSuffix } from './fenceCopy'
import { PipelineDonut } from './PipelineDonut'
import { useSalesRefreshes, refreshSalesFigures } from '../../salesFigures'
import { stageDotFor } from '../../stageDotFor'
import {
  dateLine, insightSentence, wonDelta, wonDeltaLine, wonPartialLabel, sharePct, dateBox, initials, monthLong,
  stageDisplay, truncationNote, wonMonthNote, concentrationNote, quarterLabel, likelyInQuarter, likelyTargetNote,
  revenueNotes, attentionRows, upNextItems, inboundCard, moveTarget, excludeNote,
  greeting, openingTab, tabFromSearch, searchWithTab, type HomeTab,
} from './homeView'
import { myDayItems, applyState, myDayLede, offDayNote, nextWorkingDay, type MyDayInput, type MyDayItem, type MyDayFilter } from './myDayView'
import { MyDay } from './MyDay'
import { DismissedRows, DismissedCapsule } from './DismissedRows'
import { TeamTab } from './TeamTab'
import { teamVisible, teamModel, teamLede, teamInsights } from './teamView'
import { useLogActivity } from './logActivityShared'
import { useWriteAccess } from '../../../../lib/writeAccess'
import { londonDay } from './workingDays'
import { accent, warning, surface, ink } from '../../../../theme/tokens'
import type { SalesForecast } from '../../../../api/types'
import type { SalesLead, SalesContact, SalesActivity, SalesDeal } from '../../types'

// Takes integer PENCE (the summary/deal money unit since the D9 switch).
// Per-row calls pass the deal's ORIGINAL currency (0111: non-GBP deals
// exist once captured); aggregate figures stay £ — they sum the
// GBP-comparable leg.
export function fmtMoney(pence: number, currency = 'GBP') {
  // §39.9 R3: delegates to the ONE abbreviating form in lib/money.ts.
  return compactMoney(pence, currency)
}

// The k-metric-delta contract: "color = status token" (klaxiora.css). Up and
// down read through the existing trend classes (success / danger tokens),
// flat through the neutral one, and an absent line is quiet ink — the words
// carry the meaning there, not a colour.
const DELTA_TONE_CLASS: Record<DeltaTone, string> = {
  up: 'trend-up', down: 'trend-down', flat: 'trend-neutral', absent: 'text-ink-muted',
}

/**
 * The k-metric widget for the Sales pages (S-Tier slice 3) — the OpsMetric
 * precedent verbatim: it lives WITH the Sales pages and is markup-level reuse
 * of the k-* contract, so DashboardShared's MetricCard and its non-Sales
 * consumers stay untouched. Value with an optional note line, the optional
 * `.k-metric-delta` line under the value (2.1b), and — since H-3a — the ✦
 * insight under a hairline (spec §3.1) with an optional slot beneath it (the
 * Likely card's target link or bar). On Sales Home the `.k-home` scope
 * restyles label / delta / note to the boards' sentence-case 13px
 * (klaxiora.css); Reports keeps the mono eyebrow.
 */
export function SalesMetric({ label, value, note, delta, insight, insightAction, children, onClick }: {
  label: string; value: string | number
  /** The quiet footnote row(s) under the value — one string is one row; a
   *  list is one row EACH (the Won card's partial-month label and, when the
   *  fence fires, the F-1d suffix on its own row — RULED 2026-09-27). */
  note?: string | string[]
  /** 2.1b: the trend line from trendDeltas.deltaLine — null/undefined
   *  renders no line (the wire has no member for this card). */
  delta?: DeltaLine | null
  /** H-3a: the ✦ note under the hairline; null renders no hairline. */
  insight?: string | null
  /** H-3b: a link at the end of the insight ("Exclude ›" / "Show ›"). On a
   *  card WITH a drill the card is then a div whose value area is the drill
   *  button and whose insight holds the link — two buttons side by side,
   *  never one inside the other. Absent → the card renders exactly as before. */
  insightAction?: { label: string; onClick: () => void } | null
  /** H-3a: rendered under the insight (a link, a bar) — only on a card
   *  without a drill, so no button nests inside the card's button. */
  children?: React.ReactNode
  /** SB-12: a card with a drill behind it renders as a real <button>
   *  (keyboard-reachable). Resting render is class-identical to the div —
   *  .k-metric overrides every button UA style, so the pixel pin holds
   *  (the R7-5 doctrine: affordances are hover/focus-only). */
  onClick?: () => void
}) {
  const head = (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="k-metric-label uppercase">{label}</span>
      </div>
      <div className="flex flex-col gap-[7px]">
        <div className="flex items-baseline gap-2">
          <span className="k-metric-value">{value}</span>
        </div>
        {delta && <div className={`k-metric-delta ${DELTA_TONE_CLASS[delta.tone]}`}>{delta.text}</div>}
        {note && (Array.isArray(note) ? note : [note]).map((n, i) => <div key={i} className="k-metric-note">{n}</div>)}
      </div>
    </>
  )
  const insightRow = insight && (
    <div className="k-metric-insight">✦ {insight}{insightAction && <>{' '}<button type="button" className="k-home-link" onClick={insightAction.onClick}>{insightAction.label}</button></>}</div>
  )
  const inner = (
    <>
      {head}
      {insightRow}
      {children}
    </>
  )
  if (onClick && insightAction && insight) {
    return (
      <div className="k-metric">
        <button type="button" onClick={onClick} className="k-metric-drill w-full text-left cursor-pointer focus:outline-none">{head}</button>
        {insightRow}
        {children}
      </div>
    )
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick}
        className="k-metric w-full text-left cursor-pointer focus:outline-none">
        {inner}
      </button>
    )
  }
  return <div className="k-metric">{inner}</div>
}

/** The pipeline's first stage — the one the insight sentence reasons about (a
 *  deal there has not been qualified). The spine's first open row names it;
 *  this is its DealStage VALUE (the copy prints it through stageDisplay). */
const FIRST_STAGE = 'New Lead'

/** The toast's Undo window — the SalesDealDetail precedent (spec §3.6: "Undo
 *  is available for 5 seconds"). */
const UNDO_MS = 5000
const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MON_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
/** "Sun 18 Oct" for an instant, in the browser's local day like the date tile. */
const dayLabel = (iso: string) => { const d = new Date(iso); return `${DAY_SHORT[d.getDay()]} ${d.getDate()} ${MON_SHORT[d.getMonth()]}` }

/** A board card: the boards' 24px surface with a 20px title (`.k-home-card`,
 *  klaxiora.css); the head takes an optional link on the right. */
function HomeCard({ title, sub, link, children, className = '' }: {
  title?: string; sub?: string; link?: { label: string; onClick: () => void }; children: React.ReactNode; className?: string
}) {
  return (
    <section className={`k-card k-home-card ${className}`}>
      {(title || link) && (
        <div className="flex items-baseline justify-between gap-3">
          <div className="flex flex-col gap-[2px] min-w-0">
            {title && <h2 className="k-home-card-title">{title}</h2>}
            {sub && <span className="k-home-card-sub">{sub}</span>}
          </div>
          {link && (
            <button type="button" className="k-home-link" onClick={link.onClick}>{link.label}</button>
          )}
        </div>
      )}
      {children}
    </section>
  )
}

/** The Home-local stage pill (R-1): the shared badge's chrome and its stage
 *  dot (stageDotFor on the DealStage VALUE), the label in sentence case. */
function HomeStageBadge({ stage }: { stage: string }) {
  return (
    <span className="stage-badge" style={{ background: surface.panel, color: ink.body }}>
      <span className="stage-badge-dot" style={{ background: stageDotFor(stage) }} aria-hidden="true" />
      {stageDisplay(stage)}
    </span>
  )
}

/** H-3b-2: My day's input from one Home load. */
function myDayInputOf(d: { summary: { totalPipeline: number }; deals: MyDayInput['deals']; activities: SalesActivity[]; leads: SalesLead[] | null; contacts: SalesContact[] | null; history: { items: SalesActivity[] } | null }): MyDayInput {
  return { pending: d.activities, history: d.history?.items ?? null, deals: d.deals, contacts: d.contacts, leads: d.leads, totalPipelinePence: d.summary.totalPipeline }
}

export function SalesDashboard({ onNavigate, onSelectDeal, sessionUser, sessionRole = null, onOpenPipeline, newDealOpen = false, onNewDealClose, clock }: {
  onNavigate: (page: string) => void
  onSelectDeal?: (id: string) => void
  /** The session user's display name (else email) from the shell's /v1/me
   *  state; null until loaded — the greeting renders nameless. */
  sessionUser?: string | null
  /** H-4: the session's membership role from /v1/me — owner and admin see
   *  Team; null until loaded (the deals rule still applies). */
  sessionRole?: string | null
  /** H-4: Rep detail's "Open their pipeline ›" — the board with its Owner
   *  chip set to the owner's name ('—' = unassigned). */
  onOpenPipeline?: (owner: string) => void
  /** Slice 3: the New Deal drawer's open state, held by Dashboard.tsx because
   *  the button lives in the shell's primary-action slot. */
  newDealOpen?: boolean
  onNewDealClose?: () => void
  /** The clock the date line, Up next and the month-end note read — Date by
   *  default; tests pass a fixed one (the visual suite pins the browser clock). */
  clock?: () => Date
}) {
  // H-1a: a write made anywhere in Sales (a call logged from the sheet, a
  // close on the board, the New Deal drawer's own create) asks the shared
  // store to refresh; Home re-reads its own summary on that same signal, so
  // Up next never keeps a row the sheet just completed.
  const refreshes = useSalesRefreshes()
  // SB-12: in-page drill state (the R7-1..6 shape — no URL router, no
  // cross-page filter navigation). Rendered through the SHARED
  // ReportsDrillView; this page adds no drill machinery of its own.
  const [drill, setDrill] = useState<ReportsDrill | null>(null)
  // H-3b: the exclude-test toggle — a device preference (localStorage, default
  // off). It re-reads the summary with excludeTest=true; OverviewHome never
  // passes it (the HOLD control).
  const [excludeTest, setExcludeTest] = useState<boolean>(() => readExcludeTestDeals())
  const toggleExcludeTest = () => { const next = !excludeTest; writeExcludeTestDeals(next); setExcludeTest(next) }
  // H-3b: one Move (or its Undo) in flight at a time; the guard is a ref
  // because sonner holds the Undo callback for UNDO_MS, long after this render.
  const moving = useRef(false)
  // Pending rows only (H-1a): the server's own filter, so the 200-row cap is
  // spent on rows that can be "up next". The Overview Home's feed, which
  // shares this fetcher, passes nothing and keeps its done rows. H-3a adds
  // the quarter's /forecast (for Likely in Q{n}) and the Inbound queue beside
  // it — each tolerant: a failure leaves its own card honest, not the page.
  const { data, loading, error } = useAsync(async () => {
    const ex = excludeTest ? { excludeTest: true } : undefined
    const base = await fetchSalesSummary(ex ? { activities: 'pending', excludeTest: true } : { activities: 'pending' })
    const [leads, forecast, contacts, history] = await Promise.all([
      fetchInboundLeads().then((l): SalesLead[] | null => l, (): SalesLead[] | null => null),
      base.summary.quarterStart
        ? (ex ? fetchSalesForecast({ periodKind: 'quarter', periodStart: base.summary.quarterStart }, ex) : fetchSalesForecast({ periodKind: 'quarter', periodStart: base.summary.quarterStart })).then((f): SalesForecast | null => f, (): SalesForecast | null => null)
        : Promise.resolve<SalesForecast | null>(null),
      // H-3b-2 (My day): the contacts (names, the email shape check) and the
      // activity history of every status (completedAt — follow-ups, This
      // week, the clean-up) — each tolerant like the two reads above.
      fetchContacts().then((c): SalesContact[] | null => c, (): SalesContact[] | null => null),
      fetchActivityHistory().then((h): { items: SalesActivity[]; capped: boolean } | null => h, (): { items: SalesActivity[]; capped: boolean } | null => null),
    ])
    // H-4: with the toggle on, Team reads the deals list with test deals
    // dropped by the server (tolerant: null leaves Team saying so — never the
    // unfiltered list under a toggle that says it is filtered).
    const teamDeals: SalesDeal[] | null = ex ? await fetchDeals({ excludeTest: true }).then((d): SalesDeal[] | null => d, (): SalesDeal[] | null => null) : base.deals
    return { ...base, leads, forecast, contacts, history, teamDeals }
  }, [refreshes, excludeTest])

  // H-3b-2: My day's own state. Done items keep their snapshot for the life
  // of the page (the refresh after a complete drops the activity from the
  // pending read, and the ring still counts it); snoozes are device-local
  // until the London day they come back on.
  const logActivity = useLogActivity()
  // Complete / reopen / due moves and New Deal are records-level writes
  // (lib/writeAccess; fail closed).
  const { records } = useWriteAccess()
  const [doneItems, setDoneItems] = useState<Map<string, MyDayItem>>(() => new Map())
  const [snoozed, setSnoozed] = useState<Record<string, string>>(() => readSnoozed(londonDay((clock ?? (() => new Date()))())))
  const [filter, setFilter] = useState<MyDayFilter>('all')
  // RULED 2026-10-21: dismissed rows are held back behind "{n} dismissed.
  // Show ›" — one switch for Needs attention, one for My day's list.
  const [showDismissedAttention, setShowDismissedAttention] = useState(false)
  const [showDismissedMyDay, setShowDismissedMyDay] = useState(false)
  const ticking = useRef(false)

  // H-3b-1: the tab. An address that names one wins (and is not a choice to
  // remember); otherwise the remembered tab, until the first load decides
  // whether this is the London day's first visit (homeView.openingTab).
  const [urlTab] = useState(() => tabFromSearch(window.location.search))
  const [tab, setTab] = useState<HomeTab>(() => urlTab ?? readHomeTab() ?? 'overview')
  const opened = useRef(urlTab != null)
  // H-4: Team shows only when teamVisible; a remembered or addressed Team
  // that is not visible reads as Overview (and the address follows).
  const canSeeTeam = data ? teamVisible(sessionRole, data.deals) : false
  const shownTab: HomeTab = tab === 'team' && data && !canSeeTeam ? 'overview' : tab
  useEffect(() => {
    if (!data || opened.current) return
    opened.current = true
    const at = (clock ?? (() => new Date()))()
    const today = londonDay(at)
    const open = applyState(myDayItems(myDayInputOf(data), at, fmtMoney), new Map(), readSnoozed(today)).open
    setTab(openingTab(readHomeTab(), readHomeVisitDay(), today, open))
    writeHomeVisitDay(today)
  }, [data, clock])
  // The address follows the tab while Home shows (replaceState: a tab is not
  // a Back step) and loses `?tab=` when Home goes — a deal, a nav click.
  useEffect(() => {
    const { pathname, search, hash } = window.location
    const want = searchWithTab(search, shownTab)
    if (want !== search) window.history.replaceState(window.history.state, '', `${pathname}${want}${hash}`)
  }, [shownTab])
  useEffect(() => () => {
    const { pathname, search, hash } = window.location
    const want = searchWithTab(search, null)
    if (want !== search) window.history.replaceState(window.history.state, '', `${pathname}${want}${hash}`)
  }, [])
  const chooseTab = (t: HomeTab) => { writeHomeTab(t); setTab(t) }

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  if (drill) {
    return <ReportsDrillView drill={drill} onBack={() => setDrill(null)}
      onSelectDeal={onSelectDeal} backLabel="Back to dashboard" />
  }

  const now = (clock ?? (() => new Date()))()
  const { summary, deals, activities, leads, forecast, contacts, history } = data!

  // H-3b: Move a typed meeting off a weekend / bank holiday to the next
  // working day at the same London time — ONE PUT of dueAt — then re-read
  // through the shared refresh; the toast offers Undo for UNDO_MS, which PUTs
  // the previous dueAt and re-reads again. No "moved from" copy anywhere: the
  // row simply shows its new date (R-4: no durable record of the old one).
  async function moveDue(id: string, fromDueAt: string, toDueAt: string, toLabel: string, undo = false) {
    if (moving.current) return
    moving.current = true
    try {
      await updateActivityDue(id, toDueAt)
      void refreshSalesFigures()
      if (undo) {
        toast.success(`Moved back to ${toLabel}`)
      } else {
        toast.success(`Moved to ${toLabel}`, { duration: UNDO_MS, action: { label: 'Undo', onClick: () => { void moveDue(id, toDueAt, fromDueAt, dayLabel(fromDueAt), true) } } })
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to move the meeting'))
    } finally {
      moving.current = false
    }
  }
  const openDeals = topOpenDeals(deals)
  // Mockup greets by first name ("Good morning, Dylan"); an email session
  // user greets by the full address rather than a fabricated split.
  const firstName = sessionUser && !sessionUser.includes('@')
    ? sessionUser.trim().split(/\s+/)[0]
    : sessionUser

  const wonPence = closedThisMonthPence(summary.monthlyRevenue)
  const lastBucket = summary.monthlyRevenue.length > 0
    ? summary.monthlyRevenue[summary.monthlyRevenue.length - 1] : null
  const d = summary.deltas
  // RULED 2026-09-26: one footnote for the trend line, the spec's sentence
  // with the ledger's date — never the server's reason sentence (which
  // stays on the wire for support), never an orphan "History since" line.
  const footnote = historyFootnote(summary.historySince, absentMembers(d) > 0)
  // §26 F-1: the fenced counts ride their notes as "(+k excluded)" (F-1d) —
  // nothing at 0. The revenue Σ's own fence (§22.7 (4)) rides the Won card.
  const openExcluded = excludedSuffix(summary.openExcludedNonGbp)
  const wonExcluded = excludedSuffix(summary.excludedNonGbp)
  const openDrill: ReportsDrill = { kind: 'open', openDeals: summary.openDeals, pipelinePence: summary.totalPipeline }
  const chartSeries = summary.monthlyRevenue.map(m => ({ month: m.month, wonPence: m.won }))
  // H-1a (RULED 2026-10-18): the delta compares LIKE WITH LIKE — the wire's
  // prior-month same-day-range figure — printed on the card rule.
  const wonD = wonDelta(summary.monthlyRevenue, summary.wonPriorMonthToDate)
  const won = wonDeltaLine(wonD, fmtMoney)
  // The cap sentence: every card computed from the deals list carries it when
  // the list came back AT the cap (homeView.truncationNote).
  const capNote = truncationNote(deals.length)
  // §22.7 (2)'s partial-month label is the Won card's FOOTNOTE row; the F-1d
  // suffix, when it fires, is a second row (RULED 2026-09-27).
  const wonNotes = [wonPartialLabel(wonD), wonExcluded, capNote].filter((n): n is string => !!n)
  const currentMonth = lastBucket ? lastBucket.month : null
  const quarter = summary.quarterStart ? quarterLabel(summary.quarterStart) : null
  const likely = likelyInQuarter(forecast)
  const likelyTarget = likely?.target ?? null
  // H-3b: the Win-rate note and its action, in the toggle's current state.
  const winNote = excludeNote(summary.testDeals, excludeTest)
  const attention = attentionRows(deals, summary.totalPipeline, fmtMoney)
  const next = upNextItems(activities, deals, now, fmtMoney)
  const inbound = leads ? inboundCard(leads, deals, now) : null
  const revNotes = revenueNotes(summary.monthlyRevenue, deals, fmtMoney)
  // H-3b-2: My day's list — the rules over this load, then snooze and done.
  // Its OPEN count is the switch's badge and the lede's number (H-3b-1's
  // interim due count retired with the placeholder).
  const today = londonDay(now)
  const nwd = nextWorkingDay(today)
  const myDay = applyState(myDayItems(myDayInputOf(data!), now, fmtMoney, logActivity != null), doneItems, snoozed)
  const offDay = offDayNote(today, myDay.items, myDay.done)
  const due = myDay.open
  // H-4: Team's model, only when Team is on screen (teamView holds the rules).
  const team = shownTab === 'team' && data!.teamDeals
    ? teamModel({ deals: data!.teamDeals, forecast, history: history?.items ?? null, now }, fmtMoney)
    : null

  // Done completes the activity (POST complete); Undo PUTs status pending
  // (§42 ruling 6). One in flight at a time — sonner holds the Undo callback
  // for UNDO_MS, long after this render.
  async function tick(item: MyDayItem, undo = false) {
    const a = item.activity
    if (!a || ticking.current) return
    ticking.current = true
    const reopen = undo || doneItems.has(item.key)
    try {
      if (reopen) {
        await reopenActivity(a.id)
        setDoneItems(m => { const n = new Map(m); n.delete(item.key); return n })
        toast.success('Back on your list')
      } else {
        await completeActivity(a.id)
        setDoneItems(m => new Map(m).set(item.key, item))
        toast.success('Marked done', { duration: UNDO_MS, action: { label: 'Undo', onClick: () => { void tick(item, true) } } })
      }
      void refreshSalesFigures()
    } catch (e) {
      toast.error(apiErrorMessage(e, reopen ? 'Failed to reopen the activity' : 'Failed to complete the activity'))
    } finally {
      ticking.current = false
    }
  }
  const snooze = (item: MyDayItem) => {
    const next = { ...readSnoozed(today), ...snoozed, [item.key]: nwd }
    writeSnoozed(next); setSnoozed(next)
  }
  const bringBack = (keys: string[]) => {
    const next = { ...snoozed }
    for (const k of keys) delete next[k]
    writeSnoozed(next); setSnoozed(next)
  }
  const runAction = (item: MyDayItem, el: HTMLElement) => {
    const act = item.action
    if (act.kind === 'move' && item.activity?.dueAt) void moveDue(item.activity.id, item.activity.dueAt, act.target.dueAt, act.target.label)
    else if (act.kind === 'open-deal') onSelectDeal?.(act.dealId)
    else if (act.kind === 'log-follow-up') logActivity?.open({ contact: item.contact ?? null, deal: item.deal ?? null, contacts: contacts ?? undefined, type: 'call' }, () => el)
    else if (act.kind === 'open-contacts') onNavigate('sales.contacts')
    else if (act.kind === 'open-activities') onNavigate('sales.activities')
    else if (act.kind === 'triage') onNavigate('sales.leads')
  }
  const header = (
    <div className="flex items-start justify-between gap-6 pb-2">
      <div className="flex flex-col gap-[6px] min-w-0">
        <span className="text-13 font-semibold text-ink-secondary">{dateLine(now)}</span>
        <h1 className="k-home-title">{greeting(now)}{firstName ? `, ${firstName}` : ''}</h1>
        <p className="k-home-insight">{shownTab === 'my-day' ? myDayLede(due, offDay)
          : shownTab === 'team' ? (team ? teamLede(team, history != null, fmtMoney) : 'Team figures are not available right now.')
          : insightSentence({ wonPence, deals, firstStage: FIRST_STAGE }, fmtMoney)}</p>
      </div>
      <div role="tablist" aria-label="Home view" className="k-seg shrink-0">
        <button type="button" role="tab" aria-selected={shownTab === 'overview'} onClick={() => chooseTab('overview')}>Overview</button>
        <button type="button" role="tab" aria-selected={shownTab === 'my-day'} onClick={() => chooseTab('my-day')}>
          My day{due > 0 && <span className="k-home-tab-count">{due}</span>}
        </button>
        {canSeeTeam && <button type="button" role="tab" aria-selected={shownTab === 'team'} onClick={() => chooseTab('team')}>Team</button>}
      </div>
    </div>
  )
  const newDeal = newDealOpen && records && (
    <NewDealDrawer
      initialStage="New Lead"
      onClose={() => onNewDealClose?.()}
      onCreated={() => { /* the drawer's refreshSalesFigures() re-reads this page (H-1a) */ }}
      onOpenDeal={onSelectDeal}
    />
  )
  if (shownTab === 'team') {
    // H-4: Team (spec §5), read-only.
    return (
      <div className="k-home page-enter flex flex-col gap-5">
        {header}
        {team
          ? <TeamTab model={team} insights={teamInsights(team, history != null, fmtMoney)} quarter={quarter} fmt={fmtMoney}
              capNote={truncationNote(data!.teamDeals!.length)} historyRead={history != null}
              historyNote={history?.capped ? `From the first ${ACTIVITY_HISTORY_MAX} activities only.` : null}
              onSelectDeal={onSelectDeal} onNavigate={onNavigate} onOpenPipeline={owner => onOpenPipeline?.(owner)} />
          : <ErrorState message="Team figures are not available right now: the deals list without test deals couldn't be read." />}
        {newDeal}
      </div>
    )
  }
  if (shownTab === 'my-day') {
    // H-3b-2: My day (spec §4) — the list and the right rail.
    return (
      <div className="k-home page-enter flex flex-col gap-5">
        {header}
        <MyDay list={myDay} today={today} nextWorkingDay={nwd} offDay={offDay} filter={filter} onFilter={setFilter}
          pending={activities} history={history?.items ?? null} historyCapped={history?.capped ?? false} historyCap={ACTIVITY_HISTORY_MAX}
          contacts={contacts} onAction={runAction} onTick={item => { void tick(item) }} onSnooze={snooze} onBringBack={bringBack} onNavigate={onNavigate}
          showDismissed={showDismissedMyDay} onToggleDismissed={() => setShowDismissedMyDay(v => !v)} />
        {newDeal}
      </div>
    )
  }
  const weightedNote = summary.weightedPipeline != null
    ? `${summary.openDeals} ${summary.openDeals === 1 ? 'deal' : 'deals'} · ${fmtMoney(summary.weightedPipeline)} weighted${openExcluded}`
    : `${summary.openDeals} ${summary.openDeals === 1 ? 'deal' : 'deals'} · avg ${fmtMoney(summary.avgDealSize)}${openExcluded}`

  return (
    <div className="k-home page-enter flex flex-col gap-5">
      {/* The shared header (H-3b-1) — date line, greeting, the tab's lede, the
          switch. The primary action is the shell's slot (Dashboard.tsx). */}
      {header}

      {/* Four KPI cards (spec §3.1) — three drill (SB-12); Likely in Q{n}
          carries its own link / bar, so it is a plain card. */}
      <div className="grid grid-cols-4 gap-5">
        {/* Won this month — the CURRENT bucket of the six-month series, with
            the §22.7 fact delta beneath it; drills to the month's closed-won rows. */}
        <SalesMetric label="Won this month" value={fmtMoney(wonPence)} delta={won} note={wonNotes.length ? wonNotes : undefined}
          insight={wonMonthNote(deals, currentMonth, now, wonPence, fmtMoney)}
          onClick={lastBucket ? () => setDrill({ kind: 'month', month: lastBucket.month, wonPence: lastBucket.won }) : undefined} />
        {/* Open pipeline — the fenced Σ; the count and the weighted Σ in the
            note; the 2.1b snapshot delta when the wire has a prior. */}
        <SalesMetric label="Open pipeline" value={fmtMoney(summary.totalPipeline)}
          note={capNote ? [weightedNote, capNote] : weightedNote}
          delta={excludeTest && d?.pipelinePence == null && d?.absent?.pipelinePence
            ? { text: d.absent.pipelinePence, tone: 'absent' as const }   // H-3b (RULED): under the toggle the server's reason, verbatim
            : deltaLine(d?.pipelinePence, summary.totalPipeline, fmtMoney)}
          insight={concentrationNote(deals, summary.totalPipeline, fmtMoney)}
          onClick={() => setDrill(openDrill)} />
        {/* Likely in Q{n} — won in the quarter + weightedDue (R-2); the target
            note (R-2 ruling: no bar unless every rep with open deals has one). */}
        <SalesMetric label={quarter ? `Likely in ${quarter}` : 'Likely this quarter'}
          value={likely ? fmtMoney(likely.pence) : '—'}
          note={likely ? `${fmtMoney(likely.wonPence)} won + ${fmtMoney(likely.weightedPence)} weighted` : 'Forecast not available'}
          insight={likely && quarter ? likelyTargetNote(likely.target, quarter, fmtMoney) : null}>
          {likelyTarget?.kind === 'none' && (
            <button type="button" className="k-home-link self-start" onClick={() => onNavigate('sales.forecast')}>Set target ›</button>
          )}
          {likelyTarget?.kind === 'full' && (
            <span className="k-home-share-track" role="img" aria-label={`${likelyTarget.pct}% of target`}>
              <span className="k-home-share-fill" style={{ width: `${Math.min(100, Math.max(0, likelyTarget.pct))}%`, background: accent.DEFAULT }} />
            </span>
          )}
        </SalesMetric>
        {/* Win rate · all time — the SERVER closed-cohort rate; absent renders
            an em dash, never a fake 0%; the ✦ note names the test deals (R-3). */}
        <SalesMetric label="Win rate · all time"
          value={summary.winRateClosedPct != null ? `${Math.round(summary.winRateClosedPct)}%` : '—'}
          note={summary.wonCount != null && summary.lostCount != null ? `${summary.wonCount} won · ${summary.lostCount} lost` : undefined}
          insight={winNote?.text ?? null}
          insightAction={winNote ? { label: winNote.action, onClick: toggleExcludeTest } : null}
          onClick={summary.wonCount != null && summary.lostCount != null
            ? () => setDrill({ kind: 'closed', wonCount: summary.wonCount!, lostCount: summary.lostCount! }) : undefined} />
      </div>

      {/* Row: Revenue | Pipeline ring */}
      <div className="grid grid-cols-[1.6fr_1fr] gap-5">
        <HomeCard title="Revenue" sub="Closed-won · last six months">
          <div className="flex items-center gap-4 text-12 text-ink-secondary" aria-label="Legend">
            <span className="flex items-center gap-[6px]"><span className="w-2 h-2 rounded-full" style={{ background: accent.soft }} aria-hidden="true" />Full month</span>
            <span className="flex items-center gap-[6px]"><span className="w-2 h-2 rounded-full" style={{ background: accent.DEFAULT }} aria-hidden="true" />{currentMonth ? `${monthLong(currentMonth)} to date` : 'Month to date'}</span>
          </div>
          <RevenueByMonthChart data={chartSeries} emptyStubs />
          {revNotes.length > 0 && (
            <div className="flex flex-col gap-1 pt-3 text-12 text-ink-secondary" style={{ borderTop: '1px solid var(--hair)' }}>
              {revNotes.map(n => <span key={n.text}>✦ {n.text}</span>)}
              {capNote && revNotes.some(n => n.fromList) && <span className="text-ink-muted">{capNote}</span>}
            </div>
          )}
        </HomeCard>
        <HomeCard title="Pipeline" link={{ label: 'Open board ›', onClick: () => onNavigate('sales.pipeline') }}>
          <PipelineDonut funnel={summary.funnel} values={summary.dealsByStage} weightedPence={summary.weightedPipeline} fmt={fmtMoney}
            onDrill={r => setDrill({ kind: 'stage', stage: r.stage, label: r.label, count: r.count })} />
        </HomeCard>
      </div>

      {/* Row: Needs attention | Up next (spec §3.3) */}
      <div className="grid grid-cols-[1.6fr_1fr] gap-5">
        <HomeCard title="Needs attention"
          sub={attention.count > 0 ? `${attention.count} ${attention.count === 1 ? 'deal' : 'deals'} · ${fmtMoney(attention.pence)}${capNote ? ` · ${capNote}` : ''}` : undefined}
          link={attention.count > 0 ? { label: `See all ${attention.count} ›`, onClick: () => onNavigate('sales.pipeline') } : undefined}>
          {attention.rows.length === 0 ? (
            <div className="flex items-center gap-3">
              <span className="k-home-attn" aria-hidden="true"><AlertCircle size={14} /></span>
              <div className="flex flex-col gap-[2px]">
                <span className="k-home-17 font-semibold text-ink-primary">{attention.assessed === 0 ? 'Deal health not available' : 'No deal needs attention'}</span>
                <span className="text-13 text-ink-secondary">{attention.assessed === 0
                  ? 'The deals list carries no assessment on this backend.'
                  : 'No open deal is stale, quiet, undated or past its close date.'}</span>
              </div>
            </div>
          ) : (
            <div role="list" aria-label="Needs attention" className="flex flex-col">
              {attention.rows.map(row => (
                <div key={row.deal.id} role="listitem" className="k-home-attn-row">
                  <span className="k-home-attn-stripe" style={{ background: stageDotFor(row.deal.stage) }} aria-hidden="true" />
                  <div className="flex flex-col gap-[3px] min-w-0">
                    <span className="text-14 font-semibold text-ink-primary truncate">{row.deal.name} · {fmtMoney(row.deal.amount, row.deal.currency)}</span>
                    <span className="text-13 text-ink-secondary" style={{ lineHeight: 1.4 }}>{row.reason}</span>
                  </div>
                  <button type="button" className="k-home-row-action" onClick={() => onSelectDeal?.(row.deal.id)}>{row.action}</button>
                </div>
              ))}
            </div>
          )}
          {attention.dismissed.length > 0 && (
            <DismissedRows count={attention.dismissed.length} shown={showDismissedAttention} onToggle={() => setShowDismissedAttention(v => !v)}>
              {attention.dismissed.map(row => (
                <div key={row.deal.id} role="listitem" className="k-home-attn-row is-dismissed">
                  <span className="k-home-attn-stripe" style={{ background: stageDotFor(row.deal.stage) }} aria-hidden="true" />
                  <div className="flex flex-col gap-[3px] min-w-0">
                    <span className="text-14 font-semibold text-ink-primary truncate">{row.deal.name} · {fmtMoney(row.deal.amount, row.deal.currency)}</span>
                    <span className="text-13 text-ink-secondary" style={{ lineHeight: 1.4 }}>{row.reason}</span>
                  </div>
                  <DismissedCapsule />
                  <button type="button" className="k-home-row-action" onClick={() => onSelectDeal?.(row.deal.id)}>{row.action}</button>
                </div>
              ))}
            </DismissedRows>
          )}
        </HomeCard>

        {/* Up next — the boards' inverted card: future meetings, tasks and
            close dates, three rows; the amber off-day line on a typed meeting. */}
        <section className="k-card k-home-card k-home-inverted">
          <div className="flex items-center justify-between gap-3">
            <span className="text-13 font-semibold k-home-inverted-muted">Up next</span>
            <button type="button" className="k-card-link cursor-pointer bg-transparent border-0 p-0 hover:underline k-home-inverted-link"
              onClick={() => onNavigate('sales.activities')}>See all</button>
          </div>
          {next.length > 0 ? (
            <div className="flex flex-col gap-4">
              {next.map(item => {
                const box = dateBox(item.at)
                const key = item.kind === 'activity' ? `a:${item.activity.id}` : `d:${item.deal.id}`
                const go = item.kind === 'close' && onSelectDeal ? () => onSelectDeal(item.deal.id) : undefined
                // H-3b: the Move target of a typed meeting on an off day — a plain
                // value here; the PUT runs from the click, never during render.
                const target = item.kind === 'activity' && item.amber ? moveTarget(item.activity.dueAt!) : null
                return (
                  <div key={key} className={`flex items-center gap-4${go ? ' cursor-pointer' : ''}`} onClick={go}
                    role={go ? 'button' : undefined} tabIndex={go ? 0 : undefined}
                    onKeyDown={go ? e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go() } } : undefined}>
                    <div className="k-home-datebox" aria-hidden="true">
                      <span className="k-home-datebox-mon">{box.mon}</span>
                      <span className="k-home-datebox-day">{box.day}</span>
                    </div>
                    <div className="flex flex-col gap-[2px] min-w-0">
                      <span className="k-home-17 font-semibold truncate">{item.title}</span>
                      <span className="text-13 k-home-inverted-muted">{item.sub}</span>
                      {item.amber && <span className="text-13" style={{ color: warning.DEFAULT }}>{item.amber}</span>}
                      {item.kind === 'activity' && target && records && (
                        <button type="button" className="k-home-row-action self-start mt-1"
                          onClick={e => { e.stopPropagation(); void moveDue(item.activity.id, item.activity.dueAt!, target.dueAt, target.label) }}>
                          Move to {target.label}
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="flex flex-col gap-[2px]">
              <span className="k-home-17 font-semibold">Nothing scheduled</span>
              <span className="text-13 k-home-inverted-muted">No future meetings, tasks or close dates.</span>
            </div>
          )}
          <span className="k-home-footer">Shows future meetings, tasks and close dates. Calls you've already logged live in Activities.{capNote ? ` ${capNote}` : ''}</span>
        </section>
      </div>

      {/* Row: Top open deals | Inbound (spec §3.4) */}
      <div className="grid grid-cols-[1.6fr_1fr] gap-5">
        <HomeCard title="Top open deals" sub={capNote ?? undefined} link={{ label: `See all ${summary.openDeals} ›`, onClick: () => onNavigate('sales.pipeline') }}>
          {openDeals.length === 0 ? (
            <EmptyState message="No open deals" />
          ) : (
            <div role="table" aria-label="Top open deals" className="flex flex-col">
              <div role="row" className="k-home-deal-grid k-home-deal-head">
                <span role="columnheader">Deal</span><span role="columnheader">Stage</span><span role="columnheader">Share of pipeline</span><span role="columnheader" className="text-right">Value</span>
              </div>
              {openDeals.map(deal => {
                const share = sharePct(deal.basePence, summary.totalPipeline)
                const go = () => onSelectDeal?.(deal.id)
                return (
                  <div key={deal.id} role="row" tabIndex={0} onClick={go}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go() } }}
                    className="k-home-deal-grid k-home-deal-row cursor-pointer hover:bg-surface-row-hover focus:outline-none">
                    <span role="cell" className="flex items-center gap-3 min-w-0">
                      <span className="k-home-avatar" aria-hidden="true">{initials(deal.company || deal.name)}</span>
                      <span className="flex flex-col min-w-0">
                        <span className="text-14 font-semibold text-ink-primary truncate">{deal.name}</span>
                        <span className="text-12 text-ink-secondary truncate">{deal.company}</span>
                      </span>
                    </span>
                    <span role="cell"><HomeStageBadge stage={deal.stage} /></span>
                    <span role="cell" className="flex items-center gap-[10px]">
                      <span className="k-home-share-track flex-1" aria-hidden="true">
                        {share != null && <span className="k-home-share-fill" style={{ width: `${share}%`, background: stageDotFor(deal.stage) }} />}
                      </span>
                      <span className="text-13 text-ink-secondary w-9 text-right">{share != null ? `${share}%` : '—'}</span>
                    </span>
                    <span role="cell" className="text-right font-semibold text-ink-primary">{fmtMoney(deal.amount, deal.currency)}</span>
                  </div>
                )
              })}
            </div>
          )}
        </HomeCard>

        {/* Inbound — the queue's size, its oldest lead and the account_id match. */}
        <HomeCard title={inbound ? inbound.title : leads ? 'No new leads are waiting' : 'Inbound leads'}>
          <p className="text-14 text-ink-secondary" style={{ lineHeight: 1.45, margin: 0 }}>
            {inbound ? inbound.body : leads ? 'Every lead in the queue has been triaged.' : 'Inbound leads are not available right now.'}
            {inbound && capNote ? ` ${capNote}` : ''}
          </p>
          {leads && (
            <button type="button" className="k-home-link self-start" onClick={() => onNavigate('sales.leads')}>Triage leads ›</button>
          )}
        </HomeCard>
      </div>

      {/* Deals by company is retired (Accounts S3, Home deferred item (d),
          RULED 2026-10-24): the Accounts list carries each account's open
          pipeline, furthest stage and count from the server — a truer roll-up
          than this card's free-text company grouping of the newest 200 deals. */}

      {/* The trend footnote (the spec's sentence): the ledger's first day and,
          while any trend is still pending, when trends appear — never hidden
          while the wire carries historySince; absent only on a pre-2.1b
          backend. */}
      {footnote && <p className="text-12 text-ink-muted">{footnote}</p>}

      {newDeal}
    </div>
  )
}
